const classic = (issuer, currency, ticker, options = {}) => ({
  kind: 'classic',
  issuer,
  currency,
  ticker,
  ...options,
});

const series = (issuer, ticker, options = {}) => ({
  kind: 'issuer-series',
  issuer,
  currency: '*',
  ticker,
  ...options,
});

const mpt = (issuer, issuanceId, ticker, options = {}) => ({
  kind: 'mpt',
  issuer,
  issuanceId,
  ticker,
  ...options,
});

export function encodeCurrency(value) {
  const normalized = String(value).toUpperCase();
  if (normalized.length === 3) return normalized;
  return [...normalized]
    .map((character) => character.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('')
    .padEnd(40, '0')
    .slice(0, 40)
    .toUpperCase();
}

export const registry = [
  {
    id: 'rlusd',
    name: 'Ripple USD',
    ticker: 'RLUSD',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'New York', country: 'United States', lat: 40.7128, lng: -74.006 },
    summary: 'USD-backed stablecoin issued by Standard Custody & Trust Company.',
    evidence: 'Issuer and currency are published in Ripple’s official token-address registry.',
    instruments: [classic('rMxCKbEDwqr76QuheSUMdEGf4B9xJ8m5De', encodeCurrency('RLUSD'), 'RLUSD', { unit: 'USD' })],
    sources: [
      { label: 'Official token addresses', url: 'https://docs.ripple.com/products/stablecoin/overview/token-addresses' },
    ],
  },
  {
    id: 'circle-usdc',
    name: 'Circle USDC',
    ticker: 'USDC',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Boston', country: 'United States', lat: 42.3601, lng: -71.0589 },
    summary: 'Circle-issued US dollar stablecoin on XRPL.',
    evidence: 'The public XRPL issuer is published by Circle and corroborated by Ripple.',
    instruments: [classic('rGm7WCVp9gb4jZHWTEtGUr4dd74z2XuWhE', encodeCurrency('USDC'), 'USDC', { unit: 'USD' })],
    sources: [
      { label: 'Circle launch', url: 'https://www.circle.com/blog/now-available-usdc-on-the-xrpl' },
      { label: 'Ripple announcement', url: 'https://ripple.com/ripple-press/ripple-and-circle-launch-usdc-on-the-xrp-ledger/' },
    ],
  },
  {
    id: 'straitsx-xsgd',
    name: 'StraitsX XSGD',
    ticker: 'XSGD',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Singapore', country: 'Singapore', lat: 1.3521, lng: 103.8198 },
    summary: 'Singapore-dollar stablecoin issued by StraitsX.',
    evidence: 'Issuer identity is tied to StraitsX’s XRPL launch and observable ledger state.',
    instruments: [classic('rK67JczCpaYXVtfw3qJVmqwpSfa1bYTptw', encodeCurrency('XSGD'), 'XSGD', { unit: 'SGD' })],
    sources: [
      { label: 'StraitsX XSGD', url: 'https://www.straitsx.com/xsgd' },
      { label: 'XRPL launch', url: 'https://ripple.com/ripple-press/straitsx-launches-xsgd-stablecoin-on-xrp-ledger/' },
    ],
  },
  {
    id: 'schuman-europ',
    name: 'Schuman EURØP',
    ticker: 'EURØP',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Paris', country: 'France', lat: 48.8566, lng: 2.3522 },
    summary: 'Euro-denominated electronic-money token issued by Schuman Financial.',
    evidence: 'The XRPL contract identity is published by Schuman Financial.',
    instruments: [classic('rMkEuRii9w9uBMQDnWV5AA43gvYZR9JxVK', encodeCurrency('EUROP'), 'EURØP', { unit: 'EUR' })],
    sources: [
      { label: 'Official contracts', url: 'https://schuman.io/smart-contracts/' },
    ],
  },
  {
    id: 'sgforge-eurcv',
    name: 'SG-FORGE CoinVertible',
    ticker: 'EURCV',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Paris', country: 'France', lat: 48.8647, lng: 2.349 },
    summary: 'MiCA-compliant euro stablecoin from SG-FORGE.',
    evidence: 'The issuer account is disclosed in SG-FORGE’s official white paper.',
    instruments: [classic('rUNaS5sqRuxZz6V7rBGhoSaZiVYA3ut4UL', encodeCurrency('EURCV'), 'EURCV', { unit: 'EUR' })],
    sources: [
      { label: 'Official white paper', url: 'https://www.sgforge.com/wp-content/uploads/2025/10/EURCV-White-Paper_iXBRL_202510.html' },
      { label: 'XRPL product page', url: 'https://www.sgforge.com/sgf-coinvertible-on-the-xrp-ledger/' },
    ],
  },
  {
    id: 'braza-usdb',
    name: 'Braza USDB',
    ticker: 'USDB',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'São Paulo', country: 'Brazil', lat: -23.5505, lng: -46.6333 },
    summary: 'US dollar stablecoin issued by Braza Bank.',
    evidence: 'The issuer is published in Braza’s first-party XRPL manifest and corroborated by the launch.',
    instruments: [classic('rB3y9EPnq1ZrZP3aXgfyfdXQThzdXMrLMc', encodeCurrency('USDB'), 'USDB', { unit: 'USD' })],
    sources: [
      { label: 'Braza issuer manifest', url: 'https://tokens.brazacripto.com.br/.well-known/xrp-ledger.toml' },
      { label: 'XRPL launch', url: 'https://ripple.com/ripple-press/usdb-stablecoin-on-xrp-ledger/' },
    ],
  },
  {
    id: 'braza-bbrl',
    name: 'Braza BBRL',
    ticker: 'BBRL',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'São Paulo', country: 'Brazil', lat: -23.545, lng: -46.64 },
    summary: 'Brazilian-real stablecoin issued by Braza Bank.',
    evidence: 'The issuer is published in Braza’s first-party XRPL manifest and corroborated by the launch.',
    instruments: [classic('rH5CJsqvNqZGxrMyGaqLEoMWRYcVTAPZMt', encodeCurrency('BBRL'), 'BBRL', { unit: 'BRL' })],
    sources: [
      { label: 'Braza issuer manifest', url: 'https://tokens.brazacripto.com.br/.well-known/xrp-ledger.toml' },
      { label: 'XRPL launch', url: 'https://ripple.com/ripple-press/braza-group-announces-launch-of-bbrl-stablecoin-on-the-xrp-ledger/' },
    ],
  },
  {
    id: 'audd',
    name: 'AUDD',
    ticker: 'AUDD',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Sydney', country: 'Australia', lat: -33.8688, lng: 151.2093 },
    summary: 'Australian-dollar stablecoin from AUDC Pty Ltd.',
    evidence: 'The XRPL issuer appears in AUDD’s first-party assurance materials.',
    instruments: [classic('rUN5Zxt3K1AnMRJgEWywDJT8QDMMeLH5ok', encodeCurrency('AUDD'), 'AUDD', { unit: 'AUD' })],
    sources: [
      { label: 'Supported blockchains', url: 'https://devhub.audd.digital/docs/supported-blockchains' },
      { label: 'Transparency', url: 'https://www.audd.digital/transparency/' },
    ],
  },
  {
    id: 'quantoz',
    name: 'Quantoz EURQ & USDQ',
    ticker: 'EURQ / USDQ',
    category: 'stablecoin',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Utrecht', country: 'Netherlands', lat: 52.0907, lng: 5.1214 },
    summary: 'Regulated euro and US-dollar electronic-money tokens issued by Quantoz Payments.',
    evidence: 'Quantoz publishes the shared XRPL issuer on its transparency page.',
    instruments: [
      classic('rDk1xiArDMjDqnrR2yWypwQAKg4mKnQYvs', encodeCurrency('EURQ'), 'EURQ', { unit: 'EUR' }),
      classic('rDk1xiArDMjDqnrR2yWypwQAKg4mKnQYvs', encodeCurrency('USDQ'), 'USDQ', { unit: 'USD' }),
    ],
    sources: [
      { label: 'Official transparency', url: 'https://www.quantoz.com/transparency' },
    ],
  },
  {
    id: 'bitstamp-fiat',
    name: 'Bitstamp Fiat IOUs',
    ticker: 'USD / EUR / GBP +',
    category: 'gateway',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'Luxembourg', country: 'Luxembourg', lat: 49.6116, lng: 6.1319 },
    summary: 'Redeemable fiat IOUs issued through Bitstamp’s XRPL gateway.',
    evidence: 'Bitstamp publishes the issuer and supported IOU currencies. Availability varies by jurisdiction.',
    instruments: ['USD', 'EUR', 'GBP', 'AUD', 'CHF', 'JPY'].map((currency) =>
      classic('rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B', currency, `Bitstamp ${currency}`, { unit: currency }),
    ),
    sources: [
      { label: 'Bitstamp IOUs', url: 'https://www.bitstamp.net/learn/cryptocurrency-guide/what-are-bitstamp-ious/' },
      { label: 'Gateway API', url: 'https://www.bitstamp.net/api/' },
    ],
  },
  {
    id: 'gatehub-fiat',
    name: 'GateHub Fiat IOUs',
    ticker: 'USD / EUR / GBP',
    category: 'gateway',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'London', country: 'United Kingdom', lat: 51.5074, lng: -0.1278 },
    summary: 'Fiat-denominated gateway balances issued by GateHub entities.',
    evidence: 'Issuer accounts and supported currencies are published in GateHub support documentation.',
    instruments: [
      classic('rhub8VRN55s94qWKDv6jmDy1pUykJzF3wq', 'USD', 'GateHub USD', { unit: 'USD' }),
      classic('rhub8VRN55s94qWKDv6jmDy1pUykJzF3wq', 'EUR', 'GateHub EUR', { unit: 'EUR' }),
      classic('r4GN9eEoz9K4BhMQXe4H1eYNtvtkwGdt8g', 'GBP', 'GateHub GBP', { unit: 'GBP' }),
    ],
    sources: [
      { label: 'Supported currencies', url: 'https://support.gatehub.net/hc/en-us/articles/360021426493-Supported-currencies' },
      { label: 'Gateway identities', url: 'https://support.gatehub.net/hc/en-us/articles/360021426453-Gateways' },
    ],
  },
  {
    id: 'gatehub-xau',
    name: 'GateHub / BPG XAU',
    ticker: 'XAU',
    category: 'rwa',
    availability: 'legacy',
    verification: 'verified',
    hq: { city: 'London', country: 'United Kingdom', lat: 51.515, lng: -0.11 },
    summary: 'Legacy gold IOU with remaining ledger obligations.',
    evidence: 'GateHub says the BPG gateway was frozen and XAU delisted in June 2025; residual obligations are not treated as active supply.',
    instruments: [classic('rcoef87SYMJ58NAFx7fNM5frVknmvHsvJ', 'XAU', 'XAU', { unit: 'XAU', excludeFromActive: true })],
    sources: [
      { label: 'Delisting notice', url: 'https://support.gatehub.net/hc/en-us/articles/24697584224530-Discontinuation-of-BPG-GateWay-and-Delisting-of-XAU-Token' },
    ],
  },
  {
    id: 'palau-psc',
    name: 'Palau Stablecoin Pilot',
    ticker: 'PSC',
    category: 'stablecoin',
    availability: 'pilot',
    verification: 'corroborated',
    hq: { city: 'Ngerulmud', country: 'Palau', lat: 7.5006, lng: 134.6242 },
    summary: 'Government stablecoin pilot recorded on the public XRPL.',
    evidence: 'Palau government reports describe the pilot; residual ledger balances are shown as pilot state, not active circulation.',
    instruments: [classic('rwekfW4MiS5yZjXASRBDzzPPWYKuHvKP7E', 'PSC', 'PSC', { unit: 'USD', excludeFromActive: true })],
    sources: [
      { label: 'Government phase-one report', url: 'https://www.palaugov.pw/wp-content/uploads/Republic-of-Palau-Stablecoin-Program-Phase-1-Report.pdf' },
    ],
  },
  {
    id: 'ondo-ousg',
    name: 'Ondo Short-Term US Government Treasuries',
    ticker: 'OUSG',
    category: 'rwa',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'New York', country: 'United States', lat: 40.72, lng: -73.99 },
    summary: 'Tokenized exposure to short-term US Treasury securities.',
    evidence: 'Ondo publishes the XRPL issuer in its official address registry.',
    instruments: [classic('rHuiXXjHLpMP8ZE9sSQU5aADQVWDwv6h5p', encodeCurrency('OUSG'), 'OUSG', { unit: 'OUSG' })],
    sources: [
      { label: 'Official addresses', url: 'https://docs.ondo.finance/addresses' },
      { label: 'Product page', url: 'https://ondo.finance/ousg' },
    ],
  },
  {
    id: 'openeden-tbill',
    name: 'OpenEden Tokenized Treasury Bills',
    ticker: 'TBILL',
    category: 'rwa',
    availability: 'live',
    verification: 'corroborated',
    hq: { city: 'Singapore', country: 'Singapore', lat: 1.29, lng: 103.85 },
    summary: 'Tokenized US Treasury-bill product distributed on XRPL.',
    evidence: 'The public issuer is corroborated across the launch, ledger state and institutional asset registries.',
    instruments: [
      classic('rJNE2NNz83GJYtWVLwMvchDWEon3huWnFn', 'TBL', 'TBILL', {
        unit: 'TBL',
        excludedHolders: ['rB56JZWRKvpWNeyqM3QYfZwW4fS9YEyPWM'],
        supplyNote: 'Gross obligations and non-treasury distribution are reported separately.',
      }),
    ],
    sources: [
      { label: 'XRPL launch', url: 'https://ripple.com/ripple-press/openeden-introduces-tokenized-us-treasury-bills-onto-the-xrp-ledger/' },
      { label: 'Asset registry', url: 'https://app.rwa.xyz/assets/TBILL' },
    ],
  },
  {
    id: 'guggenheim-dcp',
    name: 'Guggenheim Digital Commercial Paper',
    ticker: 'DCP series',
    category: 'rwa',
    availability: 'live',
    verification: 'corroborated',
    hq: { city: 'New York', country: 'United States', lat: 40.706, lng: -74.012 },
    summary: 'Maturity-dated digital commercial paper issued through Zeconomy.',
    evidence: 'The issuer account is corroborated with the institutional launch; individual currency codes roll by maturity.',
    instruments: [series('rnXNUqF8LuXyUdfLdezftTeEUw2UFSLw5i', 'DCP series', { unit: 'units' })],
    sources: [
      { label: 'Institutional launch', url: 'https://ripple.com/ripple-press/guggenheim-uses-zeconomy-to-issue-digital-paper-on-xrpl' },
    ],
  },
  {
    id: 'ctrl-alt-dld',
    name: 'Ctrl Alt Dubai Property and Diamonds',
    ticker: 'DLD / GIA series',
    category: 'rwa',
    availability: 'live',
    verification: 'corroborated',
    hq: { city: 'Dubai', country: 'United Arab Emirates', lat: 25.2048, lng: 55.2708 },
    summary: 'Tokenized Dubai title-deed fractions and individually identified diamond records.',
    evidence: 'Two public issuers are associated with Ctrl Alt programs; every observed series remains separate and is never summed as a dollar value.',
    instruments: [
      series('rPFRtJ5FB6p3vPDtM5AdsQ6J3X4Mvg9Kei', 'DLD property series', { unit: 'units' }),
      series('rDgoZHy4SLPs4jhJZqkyaXX4iEsEUkCAVp', 'DLD / GIA series', { unit: 'units' }),
    ],
    sources: [
      { label: 'Ctrl Alt launch', url: 'https://ripple.com/ripple-press/ripple-expands-global-custody-footprint-into-middle-east-with-ctrl-alt/' },
    ],
  },
  {
    id: 'vert-credit',
    name: 'VERT Structured Credit',
    ticker: 'SRA–SRE',
    category: 'rwa',
    availability: 'live',
    verification: 'verified',
    hq: { city: 'São Paulo', country: 'Brazil', lat: -23.56, lng: -46.65 },
    summary: 'Tokenized private-credit and agricultural-receivable series operated by VERT.',
    evidence: 'Ripple’s launch materials publish the issuer; series and current obligations are read directly from that account.',
    instruments: [series('rsj2HzZNaQNihtX2nPJSxVZuUBrNScMYXi', 'VERT series', { unit: 'units' })],
    sources: [
      { label: 'Official issuer announcement', url: 'https://ripple.com/ripple-press/tokenized-private-credit-operations-on-xrpl/' },
    ],
  },
  {
    id: 'justoken-jmwh',
    name: 'Justoken / YPF Luz Energy',
    ticker: 'JMWH',
    category: 'rwa',
    availability: 'live',
    verification: 'corroborated',
    hq: { city: 'Buenos Aires', country: 'Argentina', lat: -34.6037, lng: -58.3816 },
    summary: 'Energy-linked tokens representing measured megawatt-hours from YPF Luz.',
    evidence: 'The issuer is corroborated with Justoken’s product launch. Token units are shown without inventing a USD price.',
    instruments: [classic('r976xbKc6om7WYTFwZHByvxnYFi1y5hJXH', encodeCurrency('JMWH'), 'JMWH', { unit: 'MWh' })],
    sources: [
      { label: 'Justoken launch', url: 'https://www.justoken.com/br/press-releases/ypf-luz-and-justoken-announce-one-of-the-worlds-largest-tokenized-energy-asset-projects-on-the-xrp-ledger' },
      { label: 'Asset registry', url: 'https://app.rwa.xyz/assets/JMWH' },
    ],
  },
  {
    id: 'archax-abrdn',
    name: 'Archax / abrdn Liquidity Fund',
    ticker: 'AAULF program',
    category: 'rwa',
    availability: 'configured',
    verification: 'corroborated',
    hq: { city: 'London', country: 'United Kingdom', lat: 51.51, lng: -0.09 },
    summary: 'XRPL distribution program for abrdn’s money-market fund interests.',
    evidence: 'The public account has trust-line configuration but no current non-zero obligations; it is not counted as circulating supply.',
    instruments: [series('rKCu4CucpepQ6N89c8T5GuX2jkxzCST18Q', 'Archax fund series', { unit: 'units' })],
    sources: [
      { label: 'Program announcement', url: 'https://ripple.com/ripple-press/archax-provides-access-to-abrdn-money-market-fund-on-the-xrp-ledger-in-collaboration-with-ripple/' },
      { label: 'Asset registry', url: 'https://app.rwa.xyz/assets/AAULF' },
    ],
  },
  {
    id: 'aviva-usd-liquidity',
    name: 'Aviva Investors USD Liquidity Fund',
    ticker: 'AUS',
    category: 'rwa',
    availability: 'live',
    verification: 'corroborated',
    hq: { city: 'London', country: 'United Kingdom', lat: 51.503, lng: -0.12 },
    summary: 'Tokenized share class of Aviva Investors’ USD Liquidity Fund, issued as an XRPL MPT.',
    evidence: 'Aviva confirms the live share class; the issuance ID, issuer and supply are read from on-ledger MPT metadata.',
    instruments: [mpt('r9o37ZXw3VQyvZzYk4p6wEgGZhH14mHmr2', '064D94DE607A616746E4BF525F17A63A3D266090F95EA90A', 'AUS', { unit: 'shares' })],
    sources: [
      { label: 'Aviva Investors launch', url: 'https://www.avivainvestors.com/en-de/about/company-news/2026/07/aviva-investors-launches-tokenised-share-class-for-usd-liquidity-fund/' },
    ],
  },
  {
    id: 'mxnb',
    name: 'Bitso MXNB on XRPL',
    ticker: 'MXNB',
    category: 'stablecoin',
    availability: 'announced',
    verification: 'announced',
    hq: { city: 'Mexico City', country: 'Mexico', lat: 19.4326, lng: -99.1332 },
    summary: 'Planned XRPL issuance and permissioned-DEX integration for Bitso’s peso stablecoin.',
    evidence: 'The program is announced, but no first-party XRPL issuer address was publicly verified in this audit.',
    instruments: [],
    sources: [
      { label: 'Partnership announcement', url: 'https://ripple.com/ripple-press/ripple-and-bitso-expand-partnership/' },
      { label: 'MXNB product', url: 'https://bitso.com/co/business/products/mxnb-stablecoin' },
    ],
  },
  {
    id: 'mercado-bitcoin',
    name: 'Mercado Bitcoin RWA Program',
    ticker: 'Multiple assets',
    category: 'rwa',
    availability: 'announced',
    verification: 'announced',
    hq: { city: 'São Paulo', country: 'Brazil', lat: -23.57, lng: -46.62 },
    summary: 'Announced permissioned fixed-income and equity-income tokenization program.',
    evidence: 'Program size is issuer-announced; a comprehensive public XRPL issuer set was not verified and no value is inferred on-ledger.',
    instruments: [],
    sources: [
      { label: 'Program announcement', url: 'https://ripple.com/ripple-press/mercado-bitcoin-expands-global-access-by-tokenizing-200m-in-regulated-financial-assets-on-the-xrp-ledger/' },
    ],
  },
  {
    id: 'crx-brazil',
    name: 'CRX Tokenization Program',
    ticker: 'Multiple assets',
    category: 'rwa',
    availability: 'announced',
    verification: 'announced',
    hq: { city: 'São Paulo', country: 'Brazil', lat: -23.54, lng: -46.61 },
    summary: 'Brazilian issuance and settlement program using XRPL and Ripple Custody.',
    evidence: 'Ripple reports program-level settlement, but no complete public account inventory was verified for transaction matching.',
    instruments: [],
    sources: [
      { label: 'Brazil program update', url: 'https://ripple.com/ripple-press/ripple-deepens-commitment-to-brazil-with-expanded-payments-offering-growing-customer-momentum-and-vasp-license-application/' },
    ],
  },
];

export const publicPrograms = registry.filter((program) => program.instruments.length > 0);

export const accountIndex = new Map();
export const mptIndex = new Map();

for (const program of publicPrograms) {
  for (const instrument of program.instruments) {
    if (!accountIndex.has(instrument.issuer)) accountIndex.set(instrument.issuer, []);
    accountIndex.get(instrument.issuer).push({ program, instrument });
    if (instrument.kind === 'mpt') {
      mptIndex.set(instrument.issuanceId.toUpperCase(), { program, instrument });
    }
  }
}

export const registryAuditDate = '2026-09-02';
